"use client";

import type React from "react";
import type { RoomType } from "@/types";
import { ArrowRight, ShieldCheck, Sparkles, UploadCloud } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";

export function RoomSetup({ roomType, photoPreview, photoIssue, setPhotoPreview, setImageAspectRatio, onRoomTypeChange, onContinue }: {
  roomType: RoomType;
  photoPreview: string | null;
  photoIssue?: string;
  setPhotoPreview: (photo: string | null) => void;
  setImageAspectRatio: (aspectRatio: string) => void;
  onRoomTypeChange: (roomType: RoomType) => void;
  onContinue: () => void;
}) {
  const preparePhoto = (url: string) => {
    const img = new Image();
    if (url.startsWith("http")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      const ratio = img.width / img.height;
      const imageFormats = [
        { name: "1:1", w: 1024, h: 1024 },
        { name: "16:9", w: 1376, h: 768 },
        { name: "4:3", w: 1280, h: 960 },
        { name: "3:4", w: 960, h: 1280 },
        { name: "9:16", w: 768, h: 1376 },
        { name: "3:2", w: 1344, h: 896 },
        { name: "2:3", w: 896, h: 1344 }
      ];

      const closest = imageFormats.reduce((prev, curr) =>
        Math.abs((curr.w/curr.h) - ratio) < Math.abs((prev.w/prev.h) - ratio) ? curr : prev
      );

      setImageAspectRatio(closest.name);

      const targetWidth = closest.w;
      const targetHeight = closest.h;

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Fit the entire photo so a window or doorway at the edge is never cropped away.
        const scale = Math.min(targetWidth / img.width, targetHeight / img.height);
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        const offsetX = (targetWidth - drawWidth) / 2;
        const offsetY = (targetHeight - drawHeight) / 2;

        ctx.fillStyle = "#f4f4f4";
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        try {
          setPhotoPreview(canvas.toDataURL("image/jpeg", 0.9));
        } catch (e) {
          console.error("Canvas taint error, falling back to original", e);
          setPhotoPreview(url);
        }
      } else {
        setPhotoPreview(url);
      }
    };
    img.src = url;
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        const resultUrl = reader.result as string;
        preparePhoto(resultUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSampleRoomSelect = () => {
    const sampleUrl =
      roomType === "bedroom"
        ? "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=1376&h=768&q=80"
        : "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1376&h=768&q=80";
    setPhotoPreview(sampleUrl);
    setImageAspectRatio("16:9");
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div>
        <Badge variant="clay" className="mb-2">
          <Sparkles className="w-3 h-3 text-clay" />
          <span>Step 1 of 2 · Room Details</span>
        </Badge>
        <h1 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight">
          Photograph your space
        </h1>
        <p className="text-sm text-ink-soft mt-1">
          Upload a photo of your room to get a matched, buyable redesign.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-bold uppercase tracking-wider text-ink">
          Room Type
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              onRoomTypeChange("living");
            }}
            className={`py-3 px-4 rounded-xl border text-sm font-semibold transition-all cursor-pointer ${
              roomType === "living"
                ? "bg-ink text-paper border-ink shadow-sm"
                : "bg-paper text-ink border-line hover:bg-paper-deep"
            }`}
          >
            Living Room
          </button>
          <button
            type="button"
            onClick={() => {
              onRoomTypeChange("bedroom");
            }}
            className={`py-3 px-4 rounded-xl border text-sm font-semibold transition-all cursor-pointer ${
              roomType === "bedroom"
                ? "bg-ink text-paper border-ink shadow-sm"
                : "bg-paper text-ink border-line hover:bg-paper-deep"
            }`}
          >
            Bedroom
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {photoIssue && <div role="alert" className="rounded-xl border border-clay/30 bg-clay/10 p-4 text-sm text-ink">
          <p className="font-semibold">Try another angle</p>
          <p className="mt-1">{photoIssue}</p>
        </div>}
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold uppercase tracking-wider text-ink">
            Room Photo
          </label>
          <button
            type="button"
            onClick={handleSampleRoomSelect}
            className="text-xs font-semibold text-clay hover:underline cursor-pointer"
          >
            Use sample {roomType} photo
          </button>
        </div>

        <div className="relative border-2 border-dashed border-line hover:border-clay/60 transition-colors rounded-2xl p-8 text-center bg-paper-deep/50 flex flex-col items-center justify-center min-h-[260px]">
          {photoPreview ? (
            <div className="relative w-full aspect-video max-h-[300px] overflow-hidden rounded-xl">
              <img
                src={photoPreview}
                alt="Room Preview"
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setPhotoPreview(null)}
                className="absolute top-2 right-2 px-3 py-1 bg-ink/80 text-paper text-xs rounded-full cursor-pointer hover:bg-ink"
              >
                Change photo
              </button>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-clay/10 text-clay flex items-center justify-center mb-3">
                <UploadCloud className="w-7 h-7" />
              </div>
              <h3 className="text-base font-bold text-ink">
                Drop your room photo here, or browse
              </h3>
              <p className="text-xs text-ink-soft mt-1 max-w-xs">
                Stand in the doorway in daylight. EXIF & GPS metadata stripped automatically.
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                aria-label="Upload room photo"
              />
            </>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-paper-deep border border-line text-xs text-ink-soft">
        <ShieldCheck className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
        <p>
          Your photo is processed to create this design. Save the private room link to return to your room and shopping list.
        </p>
      </div>

      <div className="flex justify-end pt-4">
        <Button
          size="lg"
          disabled={Boolean(photoIssue)}
          onClick={() => {
            if (!photoPreview) {
              handleSampleRoomSelect();
            }
            onContinue();
          }}
          className="gap-2"
        >
          <span>Continue to Style Quiz</span>
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
